/**
 * haiku — editorial product page choreography.
 *
 * One system: expo-out easing everywhere (declared in the CSS), classes over
 * inline styles, and a strict progressive-enhancement contract — everything
 * is visible by default and `html.pe-ready` is only added once this file is
 * certain it will animate. No JS → a complete, styled, buyable page.
 *
 * Nothing here touches the cart form's markup or handlers. Feedback rides
 * the theme's own signals: the form's `submit` (busy) and <product-form>'s
 * `add-to-cart` event (success). We never intercept, never re-fetch.
 *
 * Functional helpers (frame counter, claim bar, cart feedback) run even for
 * reduced-motion visitors — they're information, not decoration. Decorative
 * motion (splits, reveals, parallax, magnet, WebGL) is gated behind both
 * prefers-reduced-motion and Shopify.designMode.
 */
(function () {
  'use strict';

  var cfg = window.peConfig || {};
  var GL_MIN = cfg.glMinWidth || 1000;
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  if (gsap && ST) gsap.registerPlugin(ST);

  /* ── display title: split into clip-boxed lines ───────────────────────── */

  function splitLines(el) {
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return [];
    var words = text.split(' ');
    el.textContent = '';
    var probes = words.map(function (w, i) {
      var s = document.createElement('span');
      s.textContent = w + (i < words.length - 1 ? ' ' : '');
      s.style.display = 'inline-block';
      el.appendChild(s);
      return s;
    });
    var rows = [], top = null;
    probes.forEach(function (s) {
      if (top === null || Math.abs(s.offsetTop - top) > 2) {
        top = s.offsetTop;
        rows.push([]);
      }
      rows[rows.length - 1].push(s.textContent);
    });
    el.textContent = '';
    return rows.map(function (r) {
      var line = document.createElement('span');
      line.className = 'pe-line';
      var inner = document.createElement('span');
      inner.textContent = r.join('');
      line.appendChild(inner);
      el.appendChild(line);
      return line;
    });
  }

  function initTitle() {
    var el = document.querySelector('[data-pe-split]');
    if (!el) return;
    var lines = splitLines(el);
    lines.forEach(function (l, i) {
      setTimeout(function () { l.classList.add('is-in'); }, 90 + i * 100);
    });
  }

  /* ── scroll reveals ────────────────────────────────────────────────────── */

  function initReveals() {
    var items = document.querySelectorAll('.pe-reveal');
    if (!items.length) return;

    function enter(el, i) {
      setTimeout(function () { el.classList.add('is-in'); }, i < 6 ? i * 70 : 0);
    }

    if (ST) {
      Array.prototype.forEach.call(items, function (el, i) {
        ST.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: function () { enter(el, i); }
        });
      });
    } else if ('IntersectionObserver' in window) {
      var seen = 0;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          enter(e.target, seen++);
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      Array.prototype.forEach.call(items, function (el) { io.observe(el); });
    } else {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-in'); });
    }
  }

  /* ── photograph entrances ──────────────────────────────────────────────
     The hero reveals on load; every later frame gets the identical wipe on
     scroll entry. CSS owns the animation (see .pe__frame rules) — this only
     hands out `.is-shown`, so the reveal works with or without GSAP and
     never depends on WebGL. */

  function initFrameReveals() {
    var frames = Array.prototype.slice.call(document.querySelectorAll('[data-pe-frame]'));
    if (!frames.length) return;

    /* A frame's wipe waits for its photograph. It used to start on a timer
       (the hero) or on scroll (the rest) whether or not the photo had
       arrived, so on a cold visit the wipe revealed an empty frame and the
       photo then popped in with no animation (zy, 2026-10-26, video). Now the
       wipe starts once the <img> has loaded and decoded — with a cap, so a
       photo that never comes cannot leave a frame invisible. The WebGL layer
       reads `is-shown` through revealFor(), so it follows. */
    function whenPhoto(f, cap, fn) {
      var img = f.querySelector('img');
      var done = false;
      var go = function () { if (!done) { done = true; fn(); } };
      if (!img) { go(); return; }
      var decoded = function () { if (img.decode) img.decode().then(go, go); else go(); };
      if (img.complete && img.naturalWidth > 0) decoded();
      else {
        img.addEventListener('load', decoded, { once: true });
        img.addEventListener('error', go, { once: true });
      }
      setTimeout(go, cap);
    }

    var hero = frames.shift();
    var heroAt = Date.now() + 160;
    whenPhoto(hero, 2500, function () {
      setTimeout(function () { hero.classList.add('is-shown'); }, Math.max(0, heroAt - Date.now()));
    });
    if (!frames.length) return;

    function show(f) { whenPhoto(f, 1500, function () { f.classList.add('is-shown'); }); }

    if (ST) {
      frames.forEach(function (f) {
        ST.create({ trigger: f, start: 'top 92%', once: true, onEnter: function () { show(f); } });
      });
    } else if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          show(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      frames.forEach(function (f) { io.observe(f); });
    } else {
      frames.forEach(show);
    }
  }

  /* ── the live frame numeral ────────────────────────────────────────────
     Tracks whichever frame owns the middle of the viewport, so the count
     always agrees with what you're looking at. rAF-throttled. */

  function initCounter() {
    var frames = Array.prototype.slice.call(document.querySelectorAll('[data-pe-frame]'));
    var out = document.querySelector('[data-pe-current]');
    if (frames.length < 2 || !out) return;

    var queued = false;

    function measure() {
      queued = false;
      var mid = window.innerHeight / 2, best = 0, bestD = Infinity;
      frames.forEach(function (f, i) {
        var r = f.getBoundingClientRect();
        var d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      var n = String(best + 1);
      if (n.length < 2) n = '0' + n;
      if (out.textContent !== n) {
        out.classList.remove('is-tick');
        // force a reflow so the tick animation can retrigger
        void out.offsetWidth;
        out.textContent = n;
        out.classList.add('is-tick');
      }
    }

    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    measure();
  }

  /* ── gallery parallax drift ────────────────────────────────────────────
     Subtle, scrubbed, desktop only. The whole frame moves (canvas included)
     so the photograph itself is never distorted — depth without lies. */

  function initParallax() {
    if (!gsap || !ST || cfg.parallax === false) return;
    if (window.innerWidth < 1000) return;
    var frames = document.querySelectorAll('[data-pe-frame]');
    if (frames.length < 2) return;
    var drift = [-2.5, -5, -1.5]; // cycle matches the a/b/c width rhythm
    Array.prototype.forEach.call(frames, function (f, i) {
      gsap.to(f, {
        yPercent: drift[i % 3],
        ease: 'none',
        scrollTrigger: { trigger: f, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    });
  }

  /* ── magnetic buy button ───────────────────────────────────────────────
     Pointer-only; never interferes with touch or keyboard; transform is
     cleared on leave. */

  function initMagnetic() {
    var btn = document.querySelector('[data-pe-magnetic]');
    if (!btn || !window.matchMedia('(hover: hover)').matches) return;

    var setX = null, setY = null;
    if (gsap) {
      setX = gsap.quickTo(btn, 'x', { duration: 0.35, ease: 'power3' });
      setY = gsap.quickTo(btn, 'y', { duration: 0.35, ease: 'power3' });
    }

    btn.addEventListener('pointermove', function (e) {
      var r = btn.getBoundingClientRect();
      var dx = ((e.clientX - (r.left + r.width / 2)) / r.width) * 8;
      var dy = ((e.clientY - (r.top + r.height / 2)) / r.height) * 5;
      if (setX) { setX(dx); setY(dy); }
      else btn.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
    });

    btn.addEventListener('pointerleave', function () {
      if (setX) { setX(0); setY(0); }
      else btn.style.transform = '';
    });
  }

  /* ── add-to-cart feedback ──────────────────────────────────────────────
     Listens, never intercepts. Busy comes from the form's own submit (the
     theme also toggles `working` on the button); success from the
     `add-to-cart` event <product-form> dispatches after the cart accepted
     the line. A safety timeout clears busy if the request errors. */

  function initCartFeedback() {
    var pf = document.querySelector('.pe__buy product-form');
    var btn = document.querySelector('.pe__buy .product__add-to-cart');
    if (!pf || !btn) return;
    var form = pf.querySelector('form');
    var safety = null;

    if (form) {
      form.addEventListener('submit', function () {
        btn.classList.add('is-busy');
        clearTimeout(safety);
        safety = setTimeout(function () { btn.classList.remove('is-busy'); }, 8000);
      });
    }

    pf.addEventListener('add-to-cart', function () {
      clearTimeout(safety);
      btn.classList.remove('is-busy');
      btn.classList.add('is-done');
      setTimeout(function () { btn.classList.remove('is-done'); }, 2200);
    });
  }

  /* ── mobile claim bar ──────────────────────────────────────────────────
     Shows below 1000px while the buy panel is off screen. It's an anchor —
     the one true cart form stays singular. */

  function initBar() {
    var bar = document.querySelector('[data-pe-bar]');
    var panel = document.querySelector('[data-pe-panel]');
    if (!bar || !panel || !('IntersectionObserver' in window)) return;

    var panelVisible = true;

    function apply() {
      bar.classList.toggle('is-on', !panelVisible && window.innerWidth < 1000);
    }

    new IntersectionObserver(function (entries) {
      panelVisible = entries[0].isIntersecting;
      apply();
    }, { rootMargin: '0px 0px -72px 0px' }).observe(panel);

    window.addEventListener('resize', apply, { passive: true });

    bar.addEventListener('click', function (e) {
      if (reduced || !panel.scrollIntoView) return;
      e.preventDefault();
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ── WebGL, maybe ──────────────────────────────────────────────────────
     Below GL_MIN the module — and Three.js with it — is never even
     downloaded. Any failure inside leaves the plain photographs untouched. */

  function maybeGL() {
    var root = document.querySelector('[data-pe-gallery]');
    if (!root || reduced || designMode) return;
    if (window.innerWidth < GL_MIN) return;
    try {
      var p = document.createElement('canvas');
      var probe = p.getContext('webgl2') || p.getContext('webgl');
      if (!probe) return;
      // Hand the probe's context back: the gallery opens one per photograph.
      var lose = probe.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    } catch (e) {
      return;
    }
    var src = root.getAttribute('data-pe-gl-src');
    if (!src) return;
    /* A module <script>, not import(src). Shopify's CDN minifies theme JS and
       rewrote import(src) here into require(src), which does not exist in a
       browser, and the catch swallowed it: the gallery never loaded for a
       visitor (measured 2026-10-17). product-gl.js boots itself once loaded;
       if it fails, the plain photographs stay. */
    var s = document.createElement('script');
    s.type = 'module';
    s.src = src;
    document.head.appendChild(s);
  }

  /* ── boot ──────────────────────────────────────────────────────────────── */

  function start() {
    var heavy = !reduced && !designMode;

    if (heavy) {
      document.documentElement.classList.add('pe-ready');
      try {
        initTitle();
        initReveals();
        initFrameReveals();
        initMagnetic();
        initParallax();
      } catch (e) {
        // any surprise: fall back to the fully visible static page
        document.documentElement.classList.remove('pe-ready');
      }
    }

    try {
      initCounter();
      initCartFeedback();
      initBar();
    } catch (e) { /* functional extras only — the page stands without them */ }

    if (heavy) maybeGL();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Hero frame viewport fit.
 *
 * Separate from the main IIFE so it runs regardless of whether the
 * choreography above bailed — the hero fitting inside the fold is layout, not
 * decoration, and shouldn't depend on GSAP or on `pe-ready`.
 */
(function () {
  'use strict';

  var MIN_VISIBLE = 240; // never squeeze the photo below this
  var BREATHE = 56; // real space below the photo, not a sliver

  function fit() {
    if (window.innerWidth < 1024) {
      document.documentElement.style.removeProperty('--pe-hero-reserve');
      return;
    }

    var frame = document.querySelector('[data-pe-frame]');
    if (!frame) return;

    /* The frame's top is set by what precedes it, not by its own height, so
       this measurement is stable even as our own cap changes its width. */
    var top = frame.getBoundingClientRect().top + (window.scrollY || 0);
    var reserve = Math.round(top + BREATHE);

    /* On short viewports, honouring the full reserve would shrink the photo to
       nothing. Cap it so a usable image always survives. */
    var maxReserve = Math.max(0, window.innerHeight - MIN_VISIBLE);
    if (reserve > maxReserve) reserve = maxReserve;

    document.documentElement.style.setProperty('--pe-hero-reserve', reserve + 'px');
  }

  var t = null;
  function queue() {
    clearTimeout(t);
    t = setTimeout(fit, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fit);
  } else {
    fit();
  }

  /* Fonts and images landing can shift the frame's top. */
  window.addEventListener('load', fit);
  window.addEventListener('resize', queue, { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
  document.addEventListener('shopify:section:load', fit);
})();
